/**
 * ComparisonRecordSelector Component
 *
 * A grouped dropdown for selecting records to compare.
 * Records are organized by lifecycle state with collapsible groups.
 *
 * Features:
 * - Mode toggle (Same Entity / Any Record)
 * - Grouped by lifecycle state
 * - Collapsible state groups
 * - Power-find search filtering
 * - "Load more" pagination per group
 */

import React, { useCallback, useEffect, useRef } from 'react'
import {
  Input,
  Button,
  Empty,
  Spin,
  Space,
  Typography,
  Badge,
  Segmented,
  Tag,
} from 'antd'
import type { InputRef } from 'antd'
import {
  SearchOutlined,
  DownOutlined,
  RightOutlined,
  LoadingOutlined,
} from '@ant-design/icons'
import { Virtuoso } from 'react-virtuoso'
import dayjs from 'dayjs'
import { useComparisonRecords } from './useComparisonRecords'
import type {
  ComparisonSelectorMode,
  RecordGroup,
} from './types'

/** Threshold for using virtualization (records count) */
const VIRTUALIZATION_THRESHOLD = 50

/** Fixed height for virtualized list items */
const VIRTUALIZED_ITEM_HEIGHT = 72

const { Text } = Typography

/**
 * Get color for lifecycle state badge.
 */
function getStateColor(state: string): string {
  switch (state) {
    case 'draft':
      return 'processing'
    case 'active':
      return 'success'
    case 'expired':
      return 'default'
    case 'superseded':
      return 'warning'
    case 'archived':
      return 'default'
    default:
      return 'default'
  }
}

/**
 * Format record display name.
 */
function formatRecordName(record: Record<string, unknown>): string {
  // Try common name fields
  const name = record.name || record.title || record.label || record.uid
  return String(name ?? 'Unnamed')
}

/**
 * Format record subtitle (effectivity dates).
 */
function formatRecordSubtitle(record: Record<string, unknown>): string {
  const from = record.effectiveFrom as string | null
  const to = record.effectiveTo as string | null

  if (!from && !to) {
    const modified = record.modifiedAt as string | null
    if (modified) {
      return `Modified ${dayjs(modified).format('MMM D, YYYY')}`
    }
    return ''
  }

  const fromStr = from ? dayjs(from).format('MMM D, YYYY') : '...'
  const toStr = to ? dayjs(to).format('MMM D, YYYY') : 'ongoing'

  return `${fromStr} → ${toStr}`
}

interface RecordItemProps {
  record: Record<string, unknown>
  isSelected: boolean
  onClick: () => void
  /** Style overrides for virtualized rendering */
  style?: React.CSSProperties
}

/**
 * Single record item in the selector list.
 * Works both in regular List and virtualized Virtuoso contexts.
 */
function RecordItem({ record, isSelected, onClick, style }: RecordItemProps): JSX.Element {
  return (
    <div
      onClick={onClick}
      style={{
        cursor: 'pointer',
        background: isSelected ? 'var(--rp-temporal-blue-soft)' : undefined,
        padding: '8px 12px',
        borderRadius: 4,
        borderBottom: '1px solid var(--rp-border-subtle)',
        ...style,
      }}
      className="comparison-record-item"
    >
      <Space direction="vertical" size={0} style={{ width: '100%' }}>
        <Text strong={isSelected}>{formatRecordName(record)}</Text>
        <Text type="secondary" style={{ fontSize: 12 }}>
          {formatRecordSubtitle(record)}
        </Text>
        {record.lifecycleVersion != null && (
          <Tag color="blue" style={{ marginTop: 4, fontSize: 11 }}>
            v{String(record.lifecycleVersion)}
          </Tag>
        )}
      </Space>
    </div>
  )
}

interface GroupSectionProps {
  group: RecordGroup
  selectedUid: string | null
  onSelectRecord: (record: Record<string, unknown>) => void
  onLoadMore: () => void
  onToggleExpanded: () => void
  isLoadingMore: boolean
}

/**
 * Collapsible group section for a lifecycle state.
 */
function GroupSection({
  group,
  selectedUid,
  onSelectRecord,
  onLoadMore,
  onToggleExpanded,
  isLoadingMore,
}: GroupSectionProps): JSX.Element {
  const hasMore = !group.isFullyLoaded && group.records.length < group.count
  const loaded = group.records.length
  const total = group.count

  return (
    <div className="comparison-group-section">
      <div
        onClick={onToggleExpanded}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '8px 12px',
          background: 'var(--rp-surface-tertiary)',
          cursor: 'pointer',
          borderRadius: 4,
          marginBottom: 4,
        }}
      >
        <Space>
          {group.isExpanded ? <DownOutlined /> : <RightOutlined />}
          <Badge status={getStateColor(group.state) as 'processing' | 'success' | 'default' | 'warning'} />
          <Text strong>{group.label}</Text>
          <Text type="secondary">({total})</Text>
        </Space>
      </div>

      {group.isExpanded && (
        <div style={{ paddingLeft: 24 }}>
          {group.records.length === 0 && !isLoadingMore ? (
            <Text type="secondary" style={{ padding: 8, display: 'block' }}>
              Click to load records...
            </Text>
          ) : group.records.length > VIRTUALIZATION_THRESHOLD ? (
            /* Virtualized list for large record sets */
            <Virtuoso
              style={{ height: Math.min(group.records.length * VIRTUALIZED_ITEM_HEIGHT, 400) }}
              data={group.records}
              itemContent={(_index, record) => (
                <RecordItem
                  key={record.uid as string}
                  record={record}
                  isSelected={selectedUid === record.uid}
                  onClick={() => onSelectRecord(record)}
                />
              )}
              endReached={() => {
                if (hasMore && !isLoadingMore) {
                  onLoadMore()
                }
              }}
            />
          ) : (
            /* Standard list for small record sets */
            <div>
              {group.records.map((record) => (
                <RecordItem
                  key={record.uid as string}
                  record={record}
                  isSelected={selectedUid === record.uid}
                  onClick={() => onSelectRecord(record)}
                />
              ))}
            </div>
          )}

          {hasMore && group.records.length <= VIRTUALIZATION_THRESHOLD && (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onLoadMore()
              }}
              loading={isLoadingMore}
              style={{ marginTop: 4 }}
            >
              Load more ({loaded}/{total})
            </Button>
          )}

          {group.records.length === 0 && group.isExpanded && !isLoadingMore && (
            <Button
              type="link"
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                onLoadMore()
              }}
            >
              Load records
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

interface ComparisonRecordSelectorProps {
  /** API endpoint for fetching records */
  endpoint: string
  /** Anchor record for same-entity comparisons */
  anchorRecord: Record<string, unknown> | null
  /** Currently selected comparison record */
  selectedRecord: Record<string, unknown> | null
  /** Current selector mode */
  selectorMode: ComparisonSelectorMode
  /** Whether to allow mode switching */
  allowModeSwitch?: boolean
  /** Callback when a record is selected */
  onSelectRecord: (record: Record<string, unknown>) => void
  /** Callback when mode changes */
  onModeChange?: (mode: ComparisonSelectorMode) => void
  /** Custom state order from metadata */
  stateOrder?: string[]
  /** Max versions for eager loading */
  maxVersionsEager?: number
  /** Pagination size */
  paginationSize?: number
  /** Compact mode for mobile displays */
  compact?: boolean
}

/**
 * Grouped dropdown selector for comparison records.
 *
 * @example
 * ```tsx
 * <ComparisonRecordSelector
 *   endpoint="/offerings"
 *   anchorRecord={currentRecord}
 *   selectedRecord={comparisonRecord}
 *   selectorMode="same_entity"
 *   onSelectRecord={setComparisonRecord}
 *   onModeChange={setSelectorMode}
 * />
 * ```
 */
export function ComparisonRecordSelector({
  endpoint,
  anchorRecord,
  selectedRecord,
  selectorMode,
  allowModeSwitch = true,
  onSelectRecord,
  onModeChange,
  stateOrder,
  maxVersionsEager,
  paginationSize,
  compact = false,
}: ComparisonRecordSelectorProps): JSX.Element {
  // Ref for search input focus
  const searchInputRef = useRef<InputRef>(null)

  // Keyboard shortcut: "/" to focus search
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // "/" focuses search (when not typing in another input)
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes((e.target as HTMLElement)?.tagName)) {
        e.preventDefault()
        searchInputRef.current?.focus()
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [])

  // Use the comparison records hook
  const {
    groups,
    totalCount,
    isLoadingCounts,
    isLoadingRecords,
    error,
    loadMoreForGroup,
    toggleGroupExpanded,
    searchRecords,
    searchTerm,
    refetch,
  } = useComparisonRecords({
    endpoint,
    anchorRecord,
    selectorMode,
    stateOrder,
    maxVersionsEager,
    paginationSize,
    enabled: !!anchorRecord,
  })

  const selectedUid = selectedRecord?.uid as string | null

  // Handle mode toggle
  const handleModeChange = useCallback(
    (value: string | number) => {
      if (onModeChange) {
        onModeChange(value as ComparisonSelectorMode)
      }
    },
    [onModeChange]
  )

  // Handle search input
  const handleSearch = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      searchRecords(e.target.value)
    },
    [searchRecords]
  )

  // Handle group expansion toggle with auto-load
  const handleToggleExpanded = useCallback(
    (state: string) => {
      const group = groups.find((g) => g.state === state)
      if (group && !group.isExpanded && group.records.length === 0) {
        // Auto-load records when expanding an empty group
        loadMoreForGroup(state)
      }
      toggleGroupExpanded(state)
    },
    [groups, loadMoreForGroup, toggleGroupExpanded]
  )

  return (
    <div className="comparison-record-selector">
      {/* Mode Toggle */}
      {allowModeSwitch && onModeChange && (
        <div style={{ marginBottom: compact ? 8 : 12 }}>
          <Segmented
            value={selectorMode}
            onChange={handleModeChange}
            size={compact ? 'small' : 'middle'}
            options={[
              { label: 'Same Entity', value: 'same_entity' },
              { label: 'Any Record', value: 'any_record' },
            ]}
            block
          />
        </div>
      )}

      {/* Search Input - Press "/" to focus */}
      <Input
        ref={searchInputRef}
        placeholder="Search records... (press / to focus)"
        prefix={<SearchOutlined />}
        value={searchTerm}
        onChange={handleSearch}
        allowClear
        size={compact ? 'small' : 'middle'}
        style={{ marginBottom: compact ? 8 : 12 }}
        aria-label="Search records"
      />

      {/* Loading State */}
      {isLoadingCounts && (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <Spin indicator={<LoadingOutlined spin />} />
          <div style={{ marginTop: 8 }}>
            <Text type="secondary">Loading records...</Text>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div style={{ padding: 12 }}>
          <div
            role="alert"
            style={{
              padding: '12px 16px',
              backgroundColor: 'var(--rp-temporal-red-soft)',
              border: '1px solid var(--rp-temporal-red-border)',
              borderRadius: 4,
            }}
          >
            <Text type="danger" strong>Failed to load records</Text>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                {error.message}
              </Text>
            </div>
            <Button
              type="link"
              size="small"
              style={{ padding: 0, marginTop: 8 }}
              onClick={refetch}
              aria-label="Retry loading records"
            >
              Try again
            </Button>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!isLoadingCounts && !error && totalCount === 0 && (
        <Empty
          description={
            selectorMode === 'same_entity'
              ? 'No other versions of this record'
              : 'No records found'
          }
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Grouped Records */}
      {!isLoadingCounts && !error && groups.length > 0 && (
        <div className="comparison-groups">
          <div style={{ marginBottom: 8 }}>
            <Text type="secondary">
              {totalCount} record{totalCount !== 1 ? 's' : ''} found
            </Text>
          </div>

          {groups.map((group) => (
            <GroupSection
              key={group.state}
              group={group}
              selectedUid={selectedUid}
              onSelectRecord={onSelectRecord}
              onLoadMore={() => loadMoreForGroup(group.state)}
              onToggleExpanded={() => handleToggleExpanded(group.state)}
              isLoadingMore={isLoadingRecords}
            />
          ))}
        </div>
      )}
    </div>
  )
}

export default ComparisonRecordSelector
